import { StaticPageLayout } from '../components/layout/StaticPageLayout'

// NOTE TO MICHAEL — review and finalize before launch:
// • The legal-name placeholder below ("Michael Devlin, a sole proprietor
//   based in Pennsylvania, USA") may need to match the actual name on
//   record with Apple, Stripe, and your business banking. Update the
//   spelling of the last name if "Devlin" is wrong, and switch to the
//   formal entity name once an LLC is filed.
// • This is a generic SaaS Terms of Service draft modeled on industry
//   norms. Have an attorney review before relying on it for actual
//   contract enforcement, particularly the limitation-of-liability,
//   indemnity, and arbitration sections.

const TERMS_MARKDOWN = `Welcome to PantryIO. These Terms of Service ("Terms") govern your access to and use of the PantryIO mobile and web applications, websites, and related services (collectively, the "Service"), provided by Michael Devlin, a sole proprietor based in Pennsylvania, USA ("PantryIO," "we," "us," or "our").

By creating an account, downloading the app, or otherwise using the Service, you agree to these Terms. If you do not agree, do not use the Service.

## 1. Acceptance of Terms

By accessing or using the Service, you confirm that you are at least 13 years old (or the minimum age required by law in your jurisdiction) and that you have the legal capacity to enter into a binding agreement. If you are using the Service on behalf of an organization or household, you represent that you have authority to bind that group to these Terms.

We may update these Terms from time to time. If we make material changes, we will notify you by email or through the Service before the changes take effect. Your continued use after the effective date constitutes acceptance of the updated Terms.

## 2. Description of Service

PantryIO is a household grocery management application that helps users track pantry inventory, plan shopping lists, scan receipts, manage recipes, and reduce food waste. Some features are available to all users; others ("Pantry Premium" or "Pantry+") require a paid subscription.

We reserve the right to modify, suspend, or discontinue any feature at any time, with or without notice. We are not liable to you or any third party for any modification, suspension, or discontinuation of the Service.

## 3. User Accounts

To use most features, you must create an account. You agree to:

- Provide accurate and complete registration information.
- Keep your password and login credentials confidential.
- Notify us promptly at support@pantryioapp.com of any unauthorized use of your account.
- Be responsible for all activity that occurs under your account.

You may share access to your household's pantry data with other household members through our invitation flow. You are responsible for the conduct of anyone you invite to your household.

## 4. Subscription and Payment

PantryIO offers free and paid tiers. Paid subscriptions ("Pantry+") are billed on a monthly or annual basis depending on the plan you select.

**Web subscriptions** are processed through Stripe. By subscribing on the web, you authorize us to charge your payment method on a recurring basis until you cancel.

**iOS subscriptions** are processed through Apple's App Store under Apple's standard subscription terms. Apple manages billing, renewals, and cancellations for App Store subscriptions.

Subscriptions automatically renew at the end of each billing period unless cancelled before the renewal date. You can cancel anytime — your access continues through the end of the period you have already paid for. See our [Refund Policy](/refund-policy) for details on refunds.

Prices are subject to change. We will notify you of any price changes before they take effect, giving you the opportunity to cancel before being charged the new rate.

## 5. User Content and Data

You retain ownership of all data you submit to the Service, including your pantry items, shopping lists, recipes, photos, and other content ("User Content").

By using the Service, you grant PantryIO a worldwide, non-exclusive, royalty-free license to use, store, reproduce, and process your User Content solely for the purpose of providing and improving the Service. We do not sell your data, and we do not use your User Content to train third-party AI models.

You are solely responsible for the User Content you submit. You represent that you have the right to share any content you upload, and that it does not violate any third party's rights or applicable law.

We may use aggregated, anonymized data (data that does not identify you or your household) for analytics, research, and product improvement.

## 6. Acceptable Use

You agree not to:

- Use the Service for any illegal purpose or in violation of any applicable law.
- Reverse engineer, decompile, or attempt to extract source code from the Service.
- Use the Service to harm, harass, or impersonate others.
- Upload viruses, malware, or other malicious code.
- Scrape, crawl, or otherwise extract data from the Service through automated means without our prior written consent.
- Resell, sublicense, or commercially exploit the Service or any portion of it without our written permission.
- Circumvent any access restrictions, rate limits, or paywalls.

We may suspend or terminate accounts that violate these rules.

## 7. Intellectual Property

The Service, including all software, design, text, logos, and trademarks, is owned by PantryIO and protected by copyright, trademark, and other laws. We grant you a limited, non-exclusive, non-transferable license to use the Service for your personal, non-commercial use.

The PantryIO name and logo are our trademarks. You may not use them without our prior written permission.

If you submit feedback, suggestions, or feature requests, you grant us the right to use that input without restriction or compensation.

## 8. Termination

You may delete your account at any time by emailing support@pantryioapp.com.

We may suspend or terminate your access to the Service at any time, with or without notice, for any reason, including violation of these Terms. Upon termination:

- Your right to use the Service ends immediately.
- We may delete your User Content after a reasonable retention period.
- Sections of these Terms that by their nature should survive termination (including ownership, disclaimers, limitation of liability, and dispute resolution) will survive.

## 9. Disclaimers and Limitation of Liability

THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

PantryIO does not guarantee that:

- The Service will be uninterrupted, error-free, or secure.
- Receipt scans, expiry predictions, recipe suggestions, or other AI-generated outputs will be accurate or complete.
- Data backups, sync, or recovery will always succeed.

You agree that PantryIO is not responsible for spoiled food, missed grocery items, dietary decisions, or any other consequence of relying on the Service. Always verify expiry dates and food safety yourself before consuming any item.

TO THE MAXIMUM EXTENT PERMITTED BY LAW, PANTRYIO'S TOTAL LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE TWELVE MONTHS BEFORE THE CLAIM AROSE, OR (B) FIFTY US DOLLARS. WE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES.

Some jurisdictions do not allow these limitations, so they may not apply to you.

## 10. Governing Law and Dispute Resolution

These Terms are governed by the laws of the Commonwealth of Pennsylvania, USA, without regard to its conflict-of-laws principles.

Any dispute arising out of or relating to these Terms or the Service will be resolved in the state or federal courts located in Pennsylvania, and you consent to the personal jurisdiction of those courts. You and PantryIO waive the right to a jury trial to the extent permitted by law.

## Contact

Questions about these Terms? Email support@pantryioapp.com.
`

export default function Terms() {
  return (
    <StaticPageLayout
      documentTitle="Terms of Service — PantryIO"
      heading="Terms of Service"
      meta={<>Last updated: April 28, 2026</>}
      markdown={TERMS_MARKDOWN}
    />
  )
}
